/**
 * In-app browser detection.
 *
 * Identifies when MBLearn is being rendered inside a third-party app's
 * embedded WebView (Gmail, Google App, Instagram, Facebook, etc.) instead
 * of a real browser like Safari or Chrome.
 *
 * Used to nudge institutional students towards entering MBLearn directly
 * from www.mblearn.app in a real browser, eliminating the psychological
 * dependency on the activation email.
 *
 * Pure, no side effects. Safe to call during render.
 */

export type InAppBrowserSource =
  | "gmail"
  | "google"
  | "instagram"
  | "facebook"
  | "ios-webview"
  | "android-webview"
  | null;

export interface InAppBrowserResult {
  isInApp: boolean;
  source: InAppBrowserSource;
  /** Human label used in copy, e.g. "Gmail", "Instagram". */
  label: string | null;
}

const LABELS: Record<Exclude<InAppBrowserSource, null>, string> = {
  gmail: "Gmail",
  google: "Google",
  instagram: "Instagram",
  facebook: "Facebook",
  "ios-webview": "el navegador interno",
  "android-webview": "el navegador interno",
};

export function detectInAppBrowser(
  userAgent?: string
): InAppBrowserResult {
  const ua =
    userAgent ??
    (typeof navigator !== "undefined" ? navigator.userAgent : "") ??
    "";

  if (!ua) return { isInApp: false, source: null, label: null };

  // Order matters: more specific apps first.
  // Gmail iOS in-app browser identifies itself as a generic iOS UA *without*
  // Safari/, plus a Gmail or GSA marker added by the host app in many cases.
  if (/\bGmail\b/i.test(ua)) return result("gmail");
  // Google App / GSA (Google Search App). On iOS this often opens a WKWebView.
  if (/\bGSA\//.test(ua)) return result("google");
  // Instagram in-app browser
  if (/Instagram/i.test(ua)) return result("instagram");
  // Facebook in-app browser
  if (/FBAN|FBAV|FB_IAB/.test(ua)) return result("facebook");

  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);

  // Android WebView is identified by `; wv)` in the UA token.
  if (isAndroid && /;\s*wv\)/.test(ua)) return result("android-webview");

  // iOS WKWebView fallback: iOS UA that lacks any real-browser marker.
  // Real browsers on iOS include Safari/, CriOS/ (Chrome), FxiOS/ (Firefox),
  // EdgiOS/ (Edge), or DuckDuckGo/. Standalone PWAs also lack Safari/, so we
  // exclude `mobile/.+ safari` standalone case by requiring no known browser.
  if (
    isIOS &&
    !/Safari\//.test(ua) &&
    !/CriOS\//.test(ua) &&
    !/FxiOS\//.test(ua) &&
    !/EdgiOS\//.test(ua) &&
    !/DuckDuckGo\//.test(ua)
  ) {
    return result("ios-webview");
  }

  return { isInApp: false, source: null, label: null };
}

function result(source: Exclude<InAppBrowserSource, null>): InAppBrowserResult {
  return { isInApp: true, source, label: LABELS[source] };
}

/** Canonical address shown to students. */
export const MBLEARN_PUBLIC_URL = "www.mblearn.app";
